/**
 * @file ExperimentFlags.tsx
 * @description This file contains the ExperimentFlags component,
 * which utilizes the ExperimentTable component to work with Experiment Flags data.
 */

import React from "react";
import { RouteComponentProps } from "@reach/router";
import { ExperimentFlag } from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import { useExperimentFlagTable } from "./useExperimentFlagTable";
import { ModalEditTable } from "../Components/ModalEditTable";
import { ModernPendingChangesControls, ModernPendingChangesPanel } from "./ModernPendingChanges";
import { Skeleton, TableSkeleton } from "../Components/Skeleton";
import { useSelector } from "react-redux";
import * as UserRedux from "../redux/user";
import "./ExperimentTable.scss";
import "./ExperimentFlags.scss";
import { EMPTY_CELL_TEXT, EXPERIMENT_ROW_HEIGHT, formatExperimentsDate } from "./utils";

const headers = [
  {
    label: "Experiment Flag",
    field: "experiment_flag",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (experimentFlag: string) => {
      return !experimentFlag || experimentFlag.length === 0;
    },
  },
  {
    label: "Description",
    field: "description",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (description: string) => {
      return !description || description.length === 0;
    },
    renderer: (data: ExperimentFlag) => {
      const desc = data.description || EMPTY_CELL_TEXT;
      const truncated = desc.length > 50 ? `${desc.slice(0, 50)}…` : desc;
      return <div className="experimentCellText">{truncated}</div>;
    },
  },
  {
    label: "Disable UI Control",
    field: "disable_ui_control",
    type: "select",
    options: "disable_ui_control",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    renderer: (data: ExperimentFlag) => {
      return String(data.disable_ui_control);
    },
  },
  {
    label: "Created",
    field: "created",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true,
    renderer: (data: ExperimentFlag) => {
      if (!data.created) {
        return <div>{EMPTY_CELL_TEXT}</div>;
      }
      return <div>{formatExperimentsDate(data.created)}</div>;
    },
  },
  {
    label: "Last Modified",
    field: "lastmodified",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true,
    renderer: (data: ExperimentFlag) => {
      if (!data.lastmodified) {
        return <div>{EMPTY_CELL_TEXT}</div>;
      }
      return <div>{formatExperimentsDate(data.lastmodified)}</div>;
    },
  },
  {
    label: "Modified By",
    field: "lastuser",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true,
  },
];

const ExperimentFlags: React.FC<RouteComponentProps> = ({ path }) => {
  const {
    originalData,
    tableData,
    setTableData,
    newExperiments,
    updatedExperiments,
    deletedExperiments,
    hasPendingChanges,
    showPendingChanges,
    setShowPendingChanges,
    saving,
    save,
    clearAllChanges,
    deletedRows,
    setDeletedRows,
    userModalInputErrorMessage,
  } = useExperimentFlagTable();

  const isAdmin = useSelector(UserRedux.isAdminSelector);

  return (
    <div className="experimentTablePageContainer">
      <div className="experimentTableOutline">
        {tableData ? (
          <div className="experimentTableContainer">
            <ModalEditTable<ExperimentFlag>
              name="Experiment Flags"
              className="experimentTable"
              rowHeight={EXPERIMENT_ROW_HEIGHT}
              headers={headers}
              tableData={tableData}
              setTableData={setTableData}
              selectorOptions={{
                disable_ui_control: [
                  { label: "True", value: "true" },
                  { label: "False", value: "false" },
                ],
              }}
              filterBar2
              deletedRows={deletedRows}
              showModalOnAdd={true}
              setDeletedRows={setDeletedRows}
              invalidText={userModalInputErrorMessage}
              customFooterButtons={[]}
              addButtonText={"Add Experiment"}
              showReorderActions={false}
              readOnly={!isAdmin}
              enableAdd={isAdmin}
              modalTitle={
                <div>
                  <div>New Experiment Flag</div>
                  <div className="modalSubtitle">
                    Note: New flags will only work if they are an exact match for a flag implemented
                    in code
                  </div>
                </div>
              }
            />
            {isAdmin && (
              <ModernPendingChangesControls
                pendingChangesCount={
                  newExperiments.length + updatedExperiments.length + deletedExperiments.length
                }
                hasPendingChanges={hasPendingChanges}
                setShowPendingChanges={setShowPendingChanges}
                saveChanges={save}
                isSaving={saving}
                clearAllChanges={clearAllChanges}
              />
            )}
          </div>
        ) : (
          <Skeleton>
            <TableSkeleton />
          </Skeleton>
        )}
      </div>
      {showPendingChanges && isAdmin && (
        <ModernPendingChangesPanel
          originalData={originalData || {}}
          pendingChanges={{
            newRows: newExperiments,
            editedRows: updatedExperiments,
            deletedRows: deletedExperiments,
          }}
          showPendingChanges={showPendingChanges}
          setShowPendingChanges={setShowPendingChanges}
          headers={headers}
        />
      )}
    </div>
  );
};

export default ExperimentFlags;
